import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { resetPassword } from 'features/auth/api/reset-password';
import useModalState from 'shared/hooks/useModalState';
import { clearLocalStorageData } from 'shared/lib/authUtils';
import {
  PASSWORD_MAX_LENGTH,
  PASSWORD_MIN_LENGTH,
  hasRequiredCharMix,
  isCommonWeakPattern,
} from 'shared/lib/passwordValidation';
import { hasSQLInjection } from 'shared/lib/secure';
import { getErrorMessage } from 'shared/utils/errorMessage';

const useNewPassword = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    isOpen: isModalOpen,
    openModal,
    closeModal: handleCloseModal,
  } = useModalState();

  const token = searchParams.get('token') || '';
  const passwordTooltip = t('password_requirements');

  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [formData, setFormData] = useState({
    token,
    password: '',
    password_confirmation: '',
  });
  const [errors, setErrors] = useState({
    password: '',
    password_confirmation: '',
    error: '',
  });

  const { mutate: submitReset, isLoading } = useMutation(
    (payload) => resetPassword(payload),
    {
      onSuccess: () => {
        openModal();
      },
      onError: (error) => {
        setErrors((prev) => ({ ...prev, error: getErrorMessage(error) }));
      },
    }
  );

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '', error: '' }));
  };

  const handleTogglePassword = () => {
    setShowPassword((prev) => !prev);
  };

  const handleTogglePasswordConfirm = () => {
    setShowPasswordConfirm((prev) => !prev);
  };

  const handleResetPassword = () => {
    const { password, password_confirmation } = formData;
    const newErrors = {};

    if (password_confirmation === '') {
      newErrors.password_confirmation = t('password_confirmation_required');
    }
    if (password === '') {
      newErrors.password = t('password_required');
    }
    if (password && password.length < PASSWORD_MIN_LENGTH) {
      newErrors.password = t('password_min_length');
    }
    if (password && password.length > PASSWORD_MAX_LENGTH) {
      newErrors.password = t('invalid_format');
    }
    if (
      password &&
      (!hasRequiredCharMix(password) || isCommonWeakPattern(password))
    ) {
      newErrors.password = t('weak_password');
    }
    if (hasSQLInjection(password_confirmation)) {
      newErrors.password_confirmation = t('invalid_format');
    }
    if (hasSQLInjection(password)) {
      newErrors.password = t('invalid_format');
    }
    if (password !== password_confirmation) {
      newErrors.password_confirmation = t('password_mismatch');
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    submitReset(formData);
  };

  const redirectLogin = () => {
    navigate('/login');
  };

  useEffect(() => {
    clearLocalStorageData();
  }, []);

  useEffect(() => {
    setFormData((prev) => ({ ...prev, token }));
  }, [token]);

  return {
    formData,
    errors,
    loading: isLoading,
    showPassword,
    showPasswordConfirm,
    passwordTooltip,
    handleChange,
    handleTogglePassword,
    handleTogglePasswordConfirm,
    handleResetPassword,
    redirectLogin,
    isModalOpen,
    handleCloseModal,
  };
};

export default useNewPassword;
